/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1790035200000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Tue Sep 22 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "sendgrid",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to SendGrid",
          "depth": 0,
          "id": "connect-to-sendgrid"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Syncing contacts",
          "depth": 1,
          "id": "syncing-contacts"
        }, {
          "value": "Sending mail",
          "depth": 1,
          "id": "sending-mail"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      h4: "h4",
      code: "code",
      h3: "h3"
    }, _provideComponents(), props.components), {CommonErrorsPreface, LiveDebugger, SyncAlerts} = _components;
    if (!CommonErrorsPreface) _missingMdxReference("CommonErrorsPreface", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            }), _jsx(_components.th, {
              children: "API Reference"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Contacts"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to Contacts in SendGrid"
            }), _jsx(_components.td, {
              children: "Upsert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://docs.sendgrid.com/api-reference/contacts/add-or-update-a-contact",
                children: "Contacts docs"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Mail"
              })
            }), _jsx(_components.td, {
              children: "Trigger one-to-one transactional emails for each record in your data model"
            }), _jsx(_components.td, {
              children: "Triggered Sends (one row = one email)"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://docs.sendgrid.com/api-reference/mail-send/mail-send",
                children: "Mail Send API"
              })
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-sendgrid",
        children: "Connect to SendGrid"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations?from=docs",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "SendGrid"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), ". You can then authenticate Hightouch to ", _jsx(_components.strong, {
          children: "SendGrid"
        }), " by entering a SendGrid ", _jsx(_components.strong, {
          children: "API Key"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["To create an API Key in SendGrid, follow the steps outlined in the ", _jsx(_components.a, {
          href: "https://docs.sendgrid.com/ui/account-and-settings/api-keys#creating-an-api-key",
          children: "SendGrid documentation"
        }), "."]
      }), "\n", _jsx(_components.h4, {
        id: "permissions",
        children: "Permissions"
      }), "\n", _jsx(_components.p, {
        children: "The permissions the API key needs depend on what you plan on syncing."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync Type"
            }), _jsx(_components.th, {
              children: "Required Permissions (Full Access)"
            }), _jsx(_components.th, {
              children: "Optional Permissions (Read Access)"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Contacts"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "Marketing"
              })
            }), _jsx(_components.td, {
              children: "-"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Mail"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "Mail Send"
              })
            }), _jsxs(_components.td, {
              children: [_jsx(_components.code, {
                children: "Template Engine"
              }), " (for email template), ", _jsx(_components.code, {
                children: "Suppressions"
              }), " (for unsubscribe groups)"]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your SendGrid destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs?from=docs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the SendGrid destination you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-contacts",
        children: "Syncing contacts"
      }), "\n", _jsx(_components.p, {
        children: "Upsert is the only available sync mode for the SendGrid destination. Use this sync mode to update existing contacts and insert new ones into SendGrid."
      }), "\n", _jsx(_components.h4, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["To match rows from your model to contacts in SendGrid, you need to select the model column that contains your contacts' ", _jsx(_components.strong, {
          children: "email"
        }), "."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["SendGrid's ", _jsx(_components.code, {
            children: "email"
          }), " field is case-sensitive. Make sure the emails in your model\nfollow a case convention, for example, all lowercase, to avoid duplicate\ncontacts. For example, if your model has contact data for the email addresses\n", _jsx(_components.code, {
            children: "john@example.com"
          }), " and ", _jsx(_components.code, {
            children: "John@example.com"
          }), ", this would create two separate\ncontacts."]
        })
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can sync columns from your model to SendGrid default and custom fields."
      }), "\n", _jsx(_components.h3, {
        id: "sending-mail",
        children: "Sending mail"
      }), "\n", _jsxs(_components.p, {
        children: ["Mail syncs send one transactional email per row in your model using SendGrid's ", _jsx(_components.a, {
          href: "https://docs.sendgrid.com/api-reference/mail-send/mail-send",
          children: "Mail Send API"
        }), ". Enter the email address, and optionally the name, that emails should be sent from. Then choose whether to send with a SendGrid template or with content you provide in the sync. You can also select an unsubscribe group so recipients can opt out of similar emails."]
      }), "\n", _jsx(_components.h4, {
        id: "personalization-fields",
        children: "Personalization fields"
      }), "\n", _jsx(_components.p, {
        children: "Map columns from your model, or static values, to the fields SendGrid uses to personalize each email."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Field"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "To"
              })
            }), _jsx(_components.td, {
              children: "The recipient's email address. Required."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsxs(_components.td, {
              children: [_jsx(_components.strong, {
                children: "CC"
              }), " and ", _jsx(_components.strong, {
                children: "BCC"
              })]
            }), _jsx(_components.td, {
              children: "Additional recipients. Map a column that contains an array of email addresses, or a single email address from a string column or a static value."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Send At"
              })
            }), _jsx(_components.td, {
              children: "When SendGrid should send the email. If you leave this unmapped, SendGrid sends the email immediately."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Custom Arguments"
              })
            }), _jsx(_components.td, {
              children: "An object of key-value pairs SendGrid attaches to the email for tracking and analytics."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Headers"
              })
            }), _jsx(_components.td, {
              children: "An object of custom email headers."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Substitutions"
              })
            }), _jsx(_components.td, {
              children: "Template syncs only. Key-value pairs that fill the placeholders in your template."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Subject"
              })
            }), _jsx(_components.td, {
              children: "Custom content syncs only. The subject line for the email."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Content"
              })
            }), _jsx(_components.td, {
              children: "Custom content syncs only. The body of the email."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Content Type"
              })
            }), _jsxs(_components.td, {
              children: ["Custom content syncs only. The MIME type of the body, for example ", _jsx(_components.code, {
                children: "text/html"
              }), ". Defaults to ", _jsx(_components.code, {
                children: "text/plain"
              }), "."]
            })]
          })]
        })]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["To copy the same address on every email, for example a shared team inbox, map\n", _jsx(_components.strong, {
            children: "BCC"
          }), " or ", _jsx(_components.strong, {
            children: "CC"
          }), " to a static value containing that email address. You don't\nneed an array column to send to a single recipient."]
        })
      }), "\n", _jsx(_components.h4, {
        id: "mail-and-tracking-settings",
        children: "Mail and tracking settings"
      }), "\n", _jsxs(_components.p, {
        children: ["Under ", _jsx(_components.strong, {
          children: "Advanced options"
        }), ", you can bypass SendGrid's list, spam, bounce, and unsubscribe management, add a custom footer, and enable sandbox mode. You can also turn on click, open, subscription, and Google Analytics tracking. These options map to the ", _jsx(_components.code, {
          children: "mail_settings"
        }), " and ", _jsx(_components.code, {
          children: "tracking_settings"
        }), " objects in the Mail Send API."]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(CommonErrorsPreface, {}), "\n", _jsx(_components.h4, {
        id: "invalid-list-id",
        children: "Invalid list id"
      }), "\n", _jsxs(_components.p, {
        children: ["When you run a sync to SendGrid for the first time, Hightouch creates a new contact list. You can find this list on the ", _jsx(_components.strong, {
          children: "Contact Lists"
        }), " page in your SendGrid workspace. If you delete this contact list, future sync runs will reject all rows with the ", _jsx(_components.code, {
          children: "invalid list id"
        }), " error."]
      }), "\n", _jsx(_components.p, {
        children: "To resolve this issue, create a new sync. This allows Hightouch to create a new contact list, which must exist for the sync to run successfully."
      }), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
